/**
 * Prepare on the objective engine — V106-1-8 (SOC 2 points of focus) and V106-5-6
 * (ISO 42001 objectives); design pass `v106-program-screens` round 3.
 *
 *   hero       covered / total, criteria fully met, the 5-part meter (met · N/A ·
 *              not met · no evidence · to decide) and legend entries that filter;
 *   criteria   Open · Met · All; groups (CC6 / clause 6 / A.6) → criteria →
 *              objectives in document order. Under Open only not-covered points
 *              show; covered ones fold to "Show N met · M not applicable";
 *   rows       paraphrase first, then "<kind> · <label> · <detail>"; the
 *              server-ranked next gap carries the "Next gap" tag; each open row's
 *              action opens an inline panel:
 *                Not met     Fix (D5): the failing result in words, Re-run the
 *                            check, Link a ticket, Accept the risk (reason
 *                            required); ISO adds the stage 1 concern + Policies
 *                No evidence Add evidence: Collect · Connect · Request
 *                To decide   Decide: Document · Interview (never "Not met")
 *              the result row keeps Undo while focus is in the row and offers the
 *              server-ranked "Next gap →" (item 1); a ticketed or risk-requested
 *              point reads "in progress" and loses the tag (item 2);
 *   deep links `#pof-cc61-05` / `#obj-612-02` open the row's panel and focus its
 *              first control after load; `?panel=connect` opens Connect with focus
 *              on "Turn on" (item 4).
 *
 * D-106-18 / D-106-19 (ADR-0078 amendment 2026-10-18): the hero explains the number ("0 reviewed ·
 * 2 checks ran, 1 result waits on review") and, for a reviewer (owner/admin or the lead), offers
 * "Review N proposed results" (approve / send back through the determination-review path; the
 * server enforces SoD). Each open row says what it lacks (review, a person, an unverified rule, a
 * reconciliation run, more tests) with its action. Confirmations promise a result only when
 * something will produce one: collector evidence → "results next hour, then your review"; a file
 * → decide with it.
 *
 * Reads (any member): objective readiness, objective inputs, the next gap (+ collectable sources,
 * in-progress points), determinations, members, the evidence library.
 * Writes (server-enforced): objective evidence upload (owner/admin/editor or the
 * lead), connector run (owner/admin), program entries (tickets / risk requests:
 * owner/admin/editor or the lead; asks), determinations + interviews.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Field, Input, Select, Spinner, Textarea } from '@aigovops/design-system';
import {
  getObjectiveReadiness, listDeterminations, listPostureConnectors, listWorkspaceMembers, proposeDetermination, reviewDetermination, runPostureConnector, sendBackDetermination, uploadObjectiveEvidence, ApiError,
} from '../../lib/api';
import { queryKeys } from '../../lib/query-keys';
import {
  deleteEvidenceArtifact, endProgramEntry, getNextGap, getObjectiveInputs, listProgramEntries, recordInterview, recordProgramEntry, type CollectSource, type NextGap, type NextGapView, type ObjectiveInput, type ProgramEntry,
} from '../../lib/program-screens-api';
import {
  KIND_GAP_LABEL, KIND_WORD, buildTree, chipList, connectorWord, evidenceWords, fmtDate, heroInputsLine, missingText, objectiveAnchor, proposedResultWords, relDays,
  type OpenKind, type Tree, type TreeCriterion, type TreeObjective,
} from '../../lib/program-screens-model';
import { Glyph } from './govramp-parts';
import { ActButton, AskButton, FileField, Gate, ResultRow, Tabs, focusWhenReady, type Person, type ResultSpec } from './objective-parts';
import '../../pages/govramp.css';
import '../../pages/today.css';
import '../../pages/program-screens.css';

export interface PrepareProps {
  slug: string;
  prefix: string;
  program: 'soc2' | 'iso-42001';
  periodId: string;
  periodLabel: string;
  hardDate: { label: string; date: string } | null;
  role: string;
  canManage: boolean;
  isAdmin: boolean;
  viewerId: string;
  lead: Person | null;
  announce: (m: string) => void;
  /** ISO: the objectives under an open stage 1 concern, with the concern text. */
  concerns?: ReadonlyMap<string, string>;
  /** ISO: objectives with a certification-body nonconformity (grade tag + its clock). */
  findings?: ReadonlyMap<string, { tag: string; clock: string }>;
  /** ISO: the SoA exclusion line. */
  soaLine?: ReactNode;
  /** D6: show only these criteria (the new counting moved them). */
  changedOnly?: ReadonlySet<string> | null;
  onClearChanged?: () => void;
  /** Rendered above the hero (D6 acknowledgement). */
  lead_in?: ReactNode;
}

type Handled = { spec: ResultSpec; next: NextGap | null | 'none' | undefined; kindAfter: 'met' | 'progress' | 'pending' | 'bad' | 'decide' | null; meta?: string };
const today = () => new Date().toISOString().slice(0, 10);
const errText = (e: unknown, fallback: string) => (e instanceof ApiError ? ((e.body as { error?: string; detail?: string } | undefined)?.detail ?? e.message) : e instanceof Error ? e.message : fallback);

/** D-106-20: a risk request's reason is the register justification (risk-acceptance-service MIN_RATIONALE_LEN). */
const RISK_REASON_MIN = 20;

/** Per objective: its live ticket, risk request and the decision on it. */
interface Tracking { ticket: Map<string, ProgramEntry>; risk: Map<string, { request: ProgramEntry; decision: ProgramEntry | null }> }
function trackingOf(entries: readonly ProgramEntry[]): Tracking {
  const ticket = new Map<string, ProgramEntry>();
  const risk = new Map<string, { request: ProgramEntry; decision: ProgramEntry | null }>();
  for (const e of entries) if (e.kind === 'objective_ticket' && e.subject_key && !ticket.has(e.subject_key)) ticket.set(e.subject_key, e);
  for (const e of entries) {
    if (e.kind !== 'objective_risk_request' || !e.subject_key || risk.has(e.subject_key)) continue;
    risk.set(e.subject_key, { request: e, decision: entries.find((d) => d.kind === 'objective_risk_decision' && d.subject_key === e.entry_id) ?? null });
  }
  return { ticket, risk };
}

const FIX_FINDING = '[data-part="fix-finding"]';
const PANEL_FIRST = '[data-panel] [role="tab"][aria-selected="true"], [data-panel] button:not([disabled]), [data-panel] input, [data-panel] select, [data-panel] textarea, [data-panel] a[href]';
/** Scroll a row into view and focus `sel` in it once rendered (else its panel's first control, else its opener). */
function focusRow(anchor: string, sel?: string) {
  const row = () => document.getElementById(anchor);
  focusWhenReady(() => {
    const r = row();
    if (!r) return null;
    const el = sel ? r.querySelector<HTMLElement>(sel) : r.querySelector<HTMLElement>(PANEL_FIRST);
    if (el) r.scrollIntoView?.({ block: 'start' });
    return el;
  }, () => row()?.querySelector<HTMLElement>(PANEL_FIRST) ?? row()?.querySelector<HTMLElement>('[data-opener]'));
}

export function ObjectivePrepare(props: PrepareProps) {
  const { slug, prefix, program, periodId } = props;
  const qc = useQueryClient();
  const location = useLocation();
  const readiness = useQuery({ queryKey: queryKeys.objectiveReadiness(slug, program, periodId), queryFn: () => getObjectiveReadiness(slug, program, periodId), retry: false, staleTime: 60_000 });
  const gapKey = ['next-gap', slug, program, periodId] as const;
  const gap = useQuery({ queryKey: gapKey, queryFn: () => getNextGap(slug, program, periodId), retry: false, staleTime: 30_000 });
  const members = useQuery({ queryKey: ['workspace-members', slug], queryFn: () => listWorkspaceMembers(slug), retry: false, staleTime: 60_000 });
  // Live tickets / risk requests / risk decisions: the row states that survive a reload (flow round 2).
  const entriesQ = useQuery({ queryKey: ['program-entries', slug, program], queryFn: () => listProgramEntries(slug, program), retry: false, staleTime: 30_000 });
  const tracking = useMemo(() => trackingOf(entriesQ.data ?? []), [entriesQ.data]);
  const people: Person[] = useMemo(() => (members.data?.members ?? []).map((m) => ({ user_id: m.user_id, name: m.display_name || m.email, email: m.email ?? null, role: m.role })), [members.data]);

  const tree: Tree | null = useMemo(() => (readiness.data ? buildTree(readiness.data.readiness) : null), [readiness.data]);
  const [seg, setSeg] = useState<'open' | 'met' | 'all'>('open');
  const [kindFilter, setKindFilter] = useState<OpenKind | null>(() => {
    const f = new URLSearchParams(location.search).get('filter');
    return f === 'not' || f === 'noev' || f === 'dec' ? f : null;
  });
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [panel, setPanel] = useState<string | null>(null);
  const [initialTab, setInitialTab] = useState<string | undefined>(undefined);
  const [handled, setHandled] = useState<ReadonlyMap<string, Handled>>(new Map());
  // D-106-19: what each point lacks + the hero summary; D-106-18: proposed results to review.
  const inputsKey = ['objective-inputs', slug, program, periodId] as const;
  const inputsQ = useQuery({ queryKey: inputsKey, queryFn: () => getObjectiveInputs(slug, program, periodId), retry: false, staleTime: 30_000 });
  const inputs = useMemo(() => new Map((inputsQ.data?.inputs.objectives ?? []).map((i) => [i.objective_id, i] as const)), [inputsQ.data]);
  const canReview = !!inputsQ.data?.can_review;
  const [reviewOpen, setReviewOpen] = useState(() => new URLSearchParams(location.search).get('panel') === 'review');
  const [decideFor, setDecideFor] = useState<ReadonlySet<string>>(new Set());
  /** Objective → the file just added for it (Decide preselects it; flow F2). */
  const [decideDoc, setDecideDoc] = useState<ReadonlyMap<string, string>>(new Map());
  const fromToday = useRef(new URLSearchParams(location.search).get('panel') === 'review');
  const reviewOpener = useRef<HTMLElement | null>(null);
  const openReview = (objectiveId?: string) => {
    const a = document.activeElement;
    reviewOpener.current = a instanceof HTMLElement && a !== document.body ? a : null;
    setReviewOpen(true);
    focusWhenReady(() => (objectiveId ? reviewItem(objectiveId)?.querySelector<HTMLElement>('button') : document.getElementById('rv-h')),
      () => document.getElementById('rv-h'));
  };
  // Flow F7: arriving from Today's review line lands on the panel (focus + scroll), not the top of the page.
  useEffect(() => { if (fromToday.current && canReview) { fromToday.current = false; openReview(); } }, [canReview]); // eslint-disable-line react-hooks/exhaustive-deps
  /** A file was added: reopen the row on Decide with that file (a file is judged by a person, never by the hourly run). */
  const reopenDecide = (o: TreeObjective, artifactId?: string) => {
    setHandled((m) => { const n = new Map(m); n.delete(o.row.objective_id); return n; });
    setDecideFor((s) => new Set([...s, o.row.objective_id]));
    if (artifactId) setDecideDoc((m) => new Map(m).set(o.row.objective_id, artifactId));
    void qc.invalidateQueries({ queryKey: ['evidence-library', slug] });
    setPanel(o.row.objective_id);
    focusRow(o.anchor);
  };
  const afterReview = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.objectiveReadiness(slug, program, periodId) });
    void qc.invalidateQueries({ queryKey: inputsKey });
    void qc.invalidateQueries({ queryKey: gapKey });
    void qc.invalidateQueries({ queryKey: ['determinations', slug, program, periodId] });
    void qc.invalidateQueries({ queryKey: ['today-programs', slug] });
  };

  const nextId = gap.data?.next?.objective_id ?? null;
  const toggle = (key: string, on: boolean) => setOpen((s) => { const n = new Set(s); if (on) n.add(key); else n.delete(key); return n; });

  /** Open a target row (its group + criterion), its panel, and focus its first control. */
  const goTo = (objectiveId: string, opts: { tab?: string; sel?: string } = {}) => {
    if (!tree) return;
    for (const g of tree.groups) for (const c of g.criteria) {
      const o = c.objectives.find((x) => x.row.objective_id === objectiveId);
      if (!o) continue;
      setOpen((s) => new Set([...s, `g:${g.group}`, `c:${c.requirement_id}`]));
      if (kindFilter && o.kind !== kindFilter && o.kind !== 'met' && o.kind !== 'na') setKindFilter(null);
      if (seg === 'met' && o.kind !== 'met' && o.kind !== 'na') setSeg('open');
      setPanel(o.kind === 'met' || o.kind === 'na' ? null : o.row.objective_id);
      setInitialTab(opts.tab);
      focusRow(o.anchor, opts.sel ?? (o.kind === 'not' ? FIX_FINDING : undefined));
      return;
    }
  };
  // The next gap is visible on arrival (aesthetic H: an unmarked next gap): its group and
  // criterion open once, without moving focus.
  const shownNext = useRef(false);
  useEffect(() => {
    if (shownNext.current || !tree || !nextId || location.hash) return;
    shownNext.current = true;
    for (const g of tree.groups) for (const c of g.criteria) {
      if (c.objectives.some((o) => o.row.objective_id === nextId)) setOpen((x) => new Set([...x, `g:${g.group}`, `c:${c.requirement_id}`]));
    }
  }, [tree, nextId, location.hash]);
  // Deep link: #pof-cc61-05 / #obj-612-02 (+ ?panel=connect) once readiness has loaded.
  const linked = useRef(false);
  useEffect(() => {
    if (linked.current || !tree) return;
    const hash = decodeURIComponent(location.hash.replace(/^#/, ''));
    if (!hash) return;
    linked.current = true;
    for (const g of tree.groups) for (const c of g.criteria) for (const o of c.objectives) {
      if (o.anchor === hash) {
        const connect = new URLSearchParams(location.search).get('panel') === 'connect';
        // After load: the browser's own scroll-to-fragment would otherwise clear the focus set here.
        window.setTimeout(() => goTo(o.row.objective_id, connect ? { tab: 'connect', sel: '[data-part="turn-on"], [data-part="connect-link"], [data-part="ask"]' } : {}), 30);
        return;
      }
    }
  });

  const onHandled = async (o: TreeObjective, h: Omit<Handled, 'next'>) => {
    setHandled((m) => new Map(m).set(o.row.objective_id, { ...h, next: undefined }));
    setPanel(null);
    props.announce(h.spec.text);
    try {
      const g = await getNextGap(slug, program, periodId, o.row.objective_id);
      setHandled((m) => { const cur = m.get(o.row.objective_id); return cur ? new Map(m).set(o.row.objective_id, { ...cur, next: g.next ?? 'none' }) : m; });
      if (g.next) props.announce(`${h.spec.text}. Next gap: ${g.next.statement}`);
    } catch {
      setHandled((m) => { const cur = m.get(o.row.objective_id); return cur ? new Map(m).set(o.row.objective_id, { ...cur, next: 'none' }) : m; });
    }
    void qc.invalidateQueries({ queryKey: gapKey });
    void qc.invalidateQueries({ queryKey: ['program-entries', slug, program] });
    void qc.invalidateQueries({ queryKey: inputsKey });
  };
  const onUndone = (o: TreeObjective) => {
    setHandled((m) => { const n = new Map(m); n.delete(o.row.objective_id); return n; });
    setPanel(o.row.objective_id);
    focusRow(o.anchor);
    props.announce('Undone');
    void qc.invalidateQueries({ queryKey: gapKey });
  };

  if (readiness.isLoading) return <Spinner label="Loading readiness" />;
  if (readiness.error || !tree) {
    return <Alert variant="error"><p>Readiness could not load.</p><ActButton onClick={() => void readiness.refetch()}>Retry</ActButton></Alert>;
  }
  const c = tree.counts;
  const isSoc = program === 'soc2';
  const noun = isSoc ? 'points of focus' : 'objectives';
  const crit = isSoc ? 'criteria' : 'requirements';
  const meterLabel = `${c.met} met, ${c.na} not applicable, ${c.not} not met, ${c.noev} no evidence, ${c.dec} to decide with a document or interview, of ${c.total}`;
  const legend: Array<[OpenKind, number, string, string]> = [['not', c.not, `${c.not} not met`, 'gr-seg--not'], ['noev', c.noev, `${c.noev} no evidence`, 'gr-seg--noev'], ['dec', c.dec, `${c.dec} to decide`, 'gr-seg--ns']];
  const days = props.hardDate ? relDays(props.hardDate.date, today()) : null;
  const filterKey = (k: OpenKind) => (k === 'not' ? 'not met' : k === 'noev' ? 'with no evidence' : 'to decide');

  const showCrit = (cr: TreeCriterion) => {
    if (props.changedOnly && !props.changedOnly.has(cr.requirement_id)) return false;
    const openN = cr.total - cr.covered;
    if (seg === 'open' && openN === 0) return false;
    if (seg === 'met' && openN > 0) return false;
    if (kindFilter && !cr.open[kindFilter]) return false;
    return true;
  };

  return (
    <div className="gr-page v6-scope" data-part="objective-prepare" data-program={program}>
      {props.lead_in}
      <section className="gr-card" aria-labelledby="rd-h">
        <div className="gr-card-head">
          <h3 id="rd-h" className="gr-hero-label">{`${isSoc ? 'Points of focus' : 'Objectives'} · ${props.periodLabel}${isSoc ? ' window' : ''}`}</h3>
          {props.hardDate ? <span className="gr-small gr-dim">{`${props.hardDate.label} `}<b className="gr-strong">{fmtDate(props.hardDate.date, today())}</b>{days ? ` · ${days}` : ''}</span> : null}
        </div>
        <p className="gr-hero-fig"><b>{`${c.covered} of ${c.total}`}</b><span className="gr-dim">{`${isSoc ? 'covered' : 'met'} · ${tree.criteria.met} of ${tree.criteria.total} ${crit} fully met`}</span></p>
        {inputsQ.data && c.covered < c.total ? (() => {
          const s = inputsQ.data.inputs.summary;
          const btn = canReview && s.awaiting_review_by_others > 0;
          const askable = !canReview && props.lead && props.lead.user_id !== props.viewerId ? props.lead : null;
          const line = heroInputsLine(s, { covered: c.covered, total: c.total, reviewButton: btn, reviewer: canReview ? null : (askable ? firstName(askable.name) : 'a lead') });
          if (!line && !btn) return null;
          return (
            <div className="v6-hero-inputs" data-part="hero-inputs">
              {line ? <span className="gr-small">{line}</span> : null}
              {btn ? (
                <button type="button" className={`gp-linkbtn gp-linkbtn--compact${reviewOpen ? '' : ' btn-primary'}`} data-part="review-open" aria-expanded={reviewOpen} aria-controls={reviewOpen ? 'review-panel' : undefined}
                  onClick={() => (reviewOpen ? setReviewOpen(false) : openReview())}>
                  {reviewCta(s.awaiting_review_by_others)}
                </button>
              ) : null}
              {/* Flow F3: someone who can't review asks the lead (no dead end). */}
              {askable && s.awaiting_review > 0 ? <AskButton slug={slug} program={program} person={askable} about={`Review ${s.awaiting_review} proposed ${s.awaiting_review === 1 ? 'result' : 'results'}`} announce={props.announce} label={`Ask ${firstName(askable.name)} to review`} /> : null}
            </div>
          );
        })() : null}
        <div className="gr-meter" role="img" aria-label={meterLabel}>
          {c.met ? <span className="gr-seg gr-seg--met" style={{ flexGrow: c.met }} /> : null}
          {c.na ? <span className="gr-seg gr-seg--na" style={{ flexGrow: c.na }} /> : null}
          {c.not ? <span className="gr-seg gr-seg--not" style={{ flexGrow: c.not }} /> : null}
          {c.noev ? <span className="gr-seg gr-seg--noev" style={{ flexGrow: c.noev }} /> : null}
          {c.dec ? <span className="gr-seg gr-seg--ns" style={{ flexGrow: c.dec }} /> : null}
        </div>
        <div className="gr-legend" role="group" aria-label={`Filter open ${noun}`}>
          <span><i className="gr-seg--met" />{`${c.met} met`}</span>
          {c.na ? <span><i className="gr-seg--na" />{`${c.na} not applicable`}</span> : null}
          {legend.filter(([, n]) => n > 0).map(([k, , text, cls]) => (
            <button key={k} type="button" aria-pressed={kindFilter === k} data-st={k} onClick={() => {
              const on = kindFilter !== k;
              setKindFilter(on ? k : null);
              if (on) { setSeg('open'); setOpen((s) => new Set([...s, ...tree.groups.filter((g) => g.open[k]).map((g) => `g:${g.group}`)])); }
              props.announce(on ? `Showing ${c[k]} ${filterKey(k)}` : `Showing all open ${noun}`);
            }}><i className={cls} aria-hidden="true" />{text}</button>
          ))}
        </div>
        {props.soaLine ?? null}
        {gap.data?.next ? (
          <p className="v6-hero-next" data-part="hero-next">
            <span className="gp-htag">Next gap</span>
            <button type="button" className="gr-textlink v6-plain" onClick={() => goTo(gap.data!.next!.objective_id)}>
              {gap.data.next.kind === 'not' ? <span aria-hidden="true">✕ </span> : null}{`${KIND_GAP_LABEL[gap.data.next.kind].replace('✕ ', '')} · ${gap.data.next.statement}`}<span className="gp-sr-only">{`, ${gap.data.next.label}`}</span>
            </button>
          </p>
        ) : null}
      </section>

      {reviewOpen && canReview ? (
        <ReviewPanel slug={slug} program={program} periodId={periodId} tree={tree} people={people} viewerId={props.viewerId} announce={props.announce} prefix={prefix}
          backToToday={new URLSearchParams(location.search).get('panel') === 'review'} next={gap.data?.next ?? null} onNext={(id) => goTo(id)}
          onClose={() => { setReviewOpen(false); const back = reviewOpener.current; focusWhenReady(() => (back?.isConnected ? back : null) ?? document.querySelector<HTMLElement>('[data-part="review-open"]') ?? document.getElementById('crit-h')); }}
          onReviewed={afterReview} />
      ) : null}

      <section className="gr-card" aria-labelledby="crit-h" id="crit-list">
        <div className="gr-card-head">
          <h3 id="crit-h" className="gr-card-h" tabIndex={-1}>{isSoc ? 'Criteria' : 'Requirements'}</h3>
          <div className="gr-seg-filter" role="group" aria-label={`Show ${crit}`}>
            {([['open', `Open · ${tree.criteria.open}`, `Showing ${tree.criteria.open} open ${crit}`], ['met', `Met · ${tree.criteria.met}`, `Showing ${tree.criteria.met} met ${crit}`], ['all', `All · ${tree.criteria.total}`, `Showing all ${tree.criteria.total} ${crit}`]] as const).map(([k, label, say]) => (
              <button key={k} type="button" aria-pressed={seg === k} onClick={() => { setSeg(k); setKindFilter(null); props.announce(say); }}>{label}</button>
            ))}
          </div>
        </div>
        {props.changedOnly ? (
          <div className="v6-chipbar" data-part="changed-chip"><span className="gp-chip gp-chip--blue">{`Changed by the new counting · ${props.changedOnly.size} ${props.changedOnly.size === 1 ? 'criterion' : 'criteria'}`}</span>
            <button type="button" className="gr-mutedlink" onClick={() => { props.onClearChanged?.(); document.getElementById('crit-h')?.focus(); props.announce(`Filter cleared · showing ${tree.criteria.open} open ${crit}`); }}>Clear</button></div>
        ) : null}
        <div className="gr-rows" data-part="crit-rows">
          {tree.groups.map((g) => {
            const crits = g.criteria.filter(showCrit);
            if (!crits.length) return null;
            const gOpen = open.has(`g:${g.group}`);
            const chips = chipList(kindFilter ? { not: 0, noev: 0, dec: 0, [kindFilter]: g.open[kindFilter] } as Record<OpenKind, number> : g.open);
            return (
              <details key={g.group} className="v6-crit" open={gOpen} data-group={g.group} onToggle={(e) => toggle(`g:${g.group}`, (e.currentTarget as HTMLDetailsElement).open)}>
                <summary>
                  <span className="v6-chev" aria-hidden="true" />
                  <span className="v6-crit-t"><span className="gr-ctl">{g.group}</span><span>{g.name}</span></span>
                  <span className="v6-crit-c">
                    <span>{seg === 'met' ? `${g.total - g.openCriteria} of ${g.total} met` : `${g.openCriteria} of ${g.total} open`}</span>
                    {seg !== 'met' ? chips.map((x) => <span key={x.k} className={`gp-chip${x.k === 'not' ? ' gp-chip--red' : ''}`}>{x.text}</span>) : null}
                  </span>
                </summary>
                {gOpen ? (
                  <div className="v6-crit-body">
                    {crits.map((cr) => (
                      <Criterion key={cr.requirement_id} cr={cr} open={open.has(`c:${cr.requirement_id}`)} onToggle={(on) => toggle(`c:${cr.requirement_id}`, on)}
                        seg={seg} kindFilter={kindFilter} {...props} people={people} tracking={tracking} gap={gap.data ?? null} nextId={nextId} panel={panel} setPanel={setPanel}
                        initialTab={initialTab} handled={handled} onHandled={onHandled} onUndone={onUndone} onNext={(n) => goTo(n.objective_id)}
                        inputs={inputs} canReview={canReview} decideFor={decideFor} decideDoc={decideDoc} openReview={openReview} reopenDecide={reopenDecide} />
                    ))}
                  </div>
                ) : null}
              </details>
            );
          })}
          {tree.groups.every((g) => !g.criteria.some(showCrit)) ? (
            <p className="gr-small gr-dim">{seg === 'met' ? `No ${crit} fully met yet · ` : `No open ${noun} here · `}
              <button type="button" className="gr-mutedlink" onClick={() => { setSeg('all'); setKindFilter(null); }}>{`Show all ${crit}`}</button></p>
          ) : null}
        </div>
      </section>

      <nav className="gp-related" aria-label="Related">
        <span className="gp-related-label" aria-hidden="true">Related</span>
        <ul className="gp-links">
          <li><Link to={`${prefix}/findings`}>Findings</Link></li>
          <li><Link to={`${prefix}/evidence`}>Evidence</Link></li>
          <li><Link to={`${prefix}/policies`}>Policies</Link></li>
          <li><Link to={`${prefix}/exceptions`}>Risk acceptances</Link></li>
        </ul>
      </nav>
    </div>
  );
}

interface CritProps extends PrepareProps {
  cr: TreeCriterion; open: boolean; onToggle: (on: boolean) => void; seg: 'open' | 'met' | 'all'; kindFilter: OpenKind | null;
  people: Person[]; tracking: Tracking; gap: NextGapView | null; nextId: string | null; panel: string | null; setPanel: (id: string | null) => void; initialTab?: string;
  handled: ReadonlyMap<string, Handled>; onHandled: (o: TreeObjective, h: Omit<Handled, 'next'>) => void; onUndone: (o: TreeObjective) => void; onNext: (g: NextGap) => void;
  /** D-106-19: what each objective lacks. */
  inputs: ReadonlyMap<string, ObjectiveInput>;
  canReview: boolean;
  decideFor: ReadonlySet<string>;
  decideDoc: ReadonlyMap<string, string>;
  openReview: (objectiveId?: string) => void;
  reopenDecide: (o: TreeObjective, artifactId?: string) => void;
}

function Criterion(p: CritProps) {
  const { cr } = p;
  const openRows = cr.objectives.filter((o) => (o.kind !== 'met' && o.kind !== 'na') || p.handled.has(o.row.objective_id));
  const shown = p.kindFilter ? openRows.filter((o) => o.kind === p.kindFilter || p.handled.has(o.row.objective_id)) : openRows;
  const covered = cr.objectives.filter((o) => (o.kind === 'met' || o.kind === 'na') && !p.handled.has(o.row.objective_id));
  const nMet = covered.filter((o) => o.kind === 'met').length, nNa = covered.length - nMet;
  const chips = chipList(cr.open);
  return (
    <details className="v6-crit" open={p.open} data-criterion={cr.ref} onToggle={(e) => p.onToggle((e.currentTarget as HTMLDetailsElement).open)}>
      <summary>
        <span className="v6-chev" aria-hidden="true" />
        <span className="v6-crit-t"><span className="gr-ctl">{cr.ref}</span><span>{cr.title}</span></span>
        <span className="v6-crit-c"><span><b className="gr-strong">{`${cr.covered} of ${cr.total}`}</b>{p.program === 'soc2' ? ' covered' : ' met'}</span>
          {p.seg !== 'met' ? chips.map((x) => <span key={x.k} className={`gp-chip${x.k === 'not' ? ' gp-chip--red' : ''}`}>{x.text}</span>) : null}</span>
      </summary>
      {p.open ? (
        <div className="v6-crit-body">
          {shown.length ? (
            <ul className="gr-flat" aria-label={`${cr.ref} open ${p.program === 'soc2' ? 'points of focus' : 'objectives'}`}>
              {shown.map((o) => <ObjectiveRow key={o.row.objective_id} o={o} {...p} />)}
            </ul>
          ) : null}
          {covered.length ? (
            <details className="v6-met-fold" open={p.seg !== 'open' || !shown.length}>
              <summary><b>Show</b>{` ${nMet} met${nNa ? ` · ${nNa} not applicable` : ''}`}</summary>
              <ul className="gr-flat" aria-label={`${cr.ref} covered`}>
                {covered.map((o) => (
                  <li key={o.row.objective_id} className="gr-ck" id={o.anchor}>
                    <span className={`gr-g gr-g--${o.kind === 'met' ? 'done' : 'na'}`} aria-hidden="true">{o.kind === 'met' ? '✓' : '–'}</span>
                    <span className="gr-ck-t">{o.row.statement}</span>
                    <span className="gr-ck-m">{`${KIND_WORD[o.kind]} · `}<span className="v6-id">{o.label}</span></span>
                    <span className="gr-ck-a"><Link className="gr-mutedlink" to={`${p.prefix}/evidence?objective=${encodeURIComponent(o.row.objective_id)}`}>View<span className="gp-sr-only">{` ${o.label}`}</span></Link></span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      ) : null}
    </details>
  );
}

function ObjectiveRow(p: CritProps & { o: TreeObjective }) {
  const { o } = p;
  const id = o.row.objective_id;
  const rowRef = useRef<HTMLLIElement>(null);
  const h = p.handled.get(id);
  const prog = p.gap?.in_progress[id] ?? null;
  const isNext = !h && p.nextId === id;
  const concern = p.concerns?.get(id) ?? null;
  const finding = p.findings?.get(id) ?? null;
  const panelOpen = p.panel === id && !h;
  const panelId = `pan-${o.anchor}`;
  const kind = h?.kindAfter === 'met' ? 'met' : h?.kindAfter === 'decide' ? 'dec' : o.kind;
  const glyph = h?.kindAfter === 'met' ? 'done' : h?.kindAfter === 'pending' ? 'act' : h?.kindAfter === 'decide' ? 'ns' : o.kind === 'not' ? 'not' : o.kind === 'noev' ? 'noev' : o.kind === 'dec' ? 'ns' : 'done';
  // D-106-19: the opener follows what the point lacks (a pending result → Review, or Ask the lead when the
  // viewer can't review; a person's call, an unreleased check or a missing comparison run → Decide).
  const inp = p.inputs.get(id) ?? null;
  const askLead = !p.canReview && p.lead && p.lead.user_id !== p.viewerId ? p.lead : null;
  const reviewerName = p.canReview ? null : askLead ? firstName(askLead.name) : 'a lead';
  const mode: 'fix' | 'review' | 'waiting' | 'decide' | 'evidence' = p.decideFor.has(id) ? 'decide' : o.kind === 'not' ? 'fix'
    : inp?.reason === 'waits_on_review' ? (p.canReview ? 'review' : askLead ? 'waiting' : 'decide')
      : inp && ['person_decides', 'rule_not_verified', 'needs_reconciliation_run'].includes(inp.reason) ? 'decide' : o.kind === 'noev' ? 'evidence' : 'decide';
  const opener = mode === 'fix' ? 'Fix' : mode === 'review' ? 'Review' : mode === 'evidence' ? 'Add evidence' : 'Decide';
  const missMeta = (o.kind === 'noev' || o.kind === 'dec') && inp ? missingText(inp, reviewerName) : null;
  const tk = p.tracking.ticket.get(id) ?? null;
  const rk = p.tracking.risk.get(id) ?? null;
  const who = (uid: string) => (uid === p.viewerId ? 'you' : p.people.find((x) => x.user_id === uid)?.name ?? 'a teammate');
  const tkMeta = tk ? `ticket ${String(tk.data.ticket_key)} · ${relDays(tk.recorded_at.slice(0, 10), today())}` : null;
  const rkMeta = rk ? (rk.decision ? `risk accepted by ${who(rk.decision.recorded_by)}` : `risk acceptance requested by ${who(rk.request.recorded_by)} · an owner or admin decides`) : null;
  const progMeta = prog === 'ticket' ? 'ticketed · in progress' : prog ? 'risk acceptance pending · in progress' : null;
  const testMeta = o.kind === 'noev' && o.row.test_keys.length ? `${o.row.test_keys.length} ${o.row.test_keys.length === 1 ? 'test needs' : 'tests need'} evidence` : null;
  const metaTail = h?.meta ?? (finding && o.kind === 'not' ? finding.clock : null) ?? tkMeta ?? rkMeta ?? progMeta ?? missMeta ?? testMeta;
  return (
    <li className="gr-ck" id={o.anchor} data-st={o.kind} data-next={isNext || undefined} ref={rowRef}>
      <Glyph status={glyph as 'done'} />
      {isNext || concern || finding ? <span className="gr-overline">{isNext ? <span className="gp-htag">Next gap</span> : null}{concern ? <> <span className="gp-htag">Stage 1 concern</span></> : null}{finding ? <> <span className="gp-htag">{finding.tag}</span></> : null}</span> : null}
      <span className="gr-ck-t">{o.row.statement}</span>
      <span className="gr-ck-m">{h?.kindAfter === 'pending' ? 'Checking' : h?.kindAfter === 'progress' ? <span className="gr-bad">Not met</span> : kind === 'not' ? <span className="gr-bad">Not met</span> : KIND_WORD[kind]}{' · '}<span className="v6-id">{o.label}</span>{metaTail ? ` · ${metaTail}` : ''}</span>
      {!h && mode === 'waiting' && askLead ? (
        <span className="gr-ck-a">
          <AskButton slug={p.slug} program={p.program} person={askLead} about={`Review the proposed result for ${o.label}`} announce={p.announce} label={`Ask ${firstName(askLead.name)} to review`} />
        </span>
      ) : !h ? (
        <span className="gr-ck-a">
          <button type="button" className={`gp-linkbtn gp-linkbtn--compact${isNext && !panelOpen ? ' btn-primary' : ''}`} aria-expanded={mode === 'review' ? undefined : panelOpen} aria-controls={mode !== 'review' && panelOpen ? panelId : undefined} data-opener data-mode={mode}
            onClick={() => {
              if (mode === 'review') { p.openReview(id); return; }
              p.setPanel(panelOpen ? null : id);
              // Opening moves focus into the panel (its selected tab or first control).
              if (!panelOpen) focusRow(o.anchor, mode === 'fix' ? FIX_FINDING : undefined);
            }}>{opener}<span className="gp-sr-only">{` ${o.label}`}</span></button>
        </span>
      ) : null}
      {panelOpen ? (
        <div className="v6-act" id={panelId} data-panel onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); p.setPanel(null); window.setTimeout(() => rowRef.current?.querySelector<HTMLElement>('[data-opener]')?.focus(), 0); } }}>
          {mode === 'fix' ? <FixPanel {...p} concern={concern} />
            : mode === 'evidence' ? <EvidencePanel {...p} initialTab={p.initialTab ?? (inp?.reason === 'needs_reconciliation_run' ? 'connect' : undefined)} />
              : <DecidePanel {...p} />}
        </div>
      ) : null}
      {!h && rk && !rk.decision && p.isAdmin && rk.request.recorded_by !== p.viewerId ? <RiskDecision p={p} o={o} request={rk.request} who={who(rk.request.recorded_by)} /> : null}
      {h ? <ResultRow spec={h.spec} next={h.next} onNext={p.onNext} onUndone={() => p.onUndone(o)} rowRef={rowRef} /> : null}
    </li>
  );
}

type PanelProps = CritProps & { o: TreeObjective };

const canEvidence = (p: PanelProps) => p.canManage || ['owner', 'admin', 'editor'].includes(p.role);

function GateFor({ p, verb }: { p: PanelProps; verb: string }) {
  const lead = p.lead;
  return <Gate id={`g-${p.o.anchor}`} who={p.program === 'soc2' ? 'An editor or the SOC 2 lead' : 'An editor or the ISO lead'} verb={verb}
    ask={lead && lead.user_id !== p.viewerId ? <AskButton slug={p.slug} program={p.program} person={lead} about={`${verb} · ${p.o.label}`} announce={p.announce} /> : null} />;
}

// ---- Fix (D5) -------------------------------------------------------------------------------

function FixPanel(p: PanelProps & { concern: string | null }) {
  const { o, slug, program, periodId } = p;
  const dets = useQuery({ queryKey: queryKeys.determinations(slug, program, periodId, 'all'), queryFn: () => listDeterminations(slug, program, periodId), retry: false, staleTime: 30_000 });
  const det = dets.data?.determinations.find((d) => d.determination_id === o.row.determination_id) ?? null;
  const src = (p.gap?.collect[o.row.objective_id] ?? []).find((s) => s.connector_id) ?? null;
  const [sub, setSub] = useState<null | 'ticket' | 'risk'>(null);
  const [decide, setDecide] = useState(false);
  const tk = p.tracking.ticket.get(o.row.objective_id) ?? null;
  const [running, setRunning] = useState<'idle' | 'running' | 'still_running' | 'ran' | 'error'>('idle');
  const ticketBtn = useRef<HTMLButtonElement>(null);
  const qc = useQueryClient();
  const rerun = async () => {
    if (!src?.connector_id || running === 'running') return;
    setRunning('running');
    p.announce('Re-running the check');
    try {
      const before = (await listPostureConnectors(slug)).connectors.find((c) => c.connector_id === src.connector_id)?.last_run_at ?? null;
      await runPostureConnector(slug, src.connector_id);
      let ran = false;
      for (let i = 0; i < 20 && !ran; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const now = (await listPostureConnectors(slug)).connectors.find((c) => c.connector_id === src.connector_id)?.last_run_at ?? null;
        ran = !!now && now !== before;
      }
      // D-106-19: a fresh check result is judged by the hourly run and then reviewed; nothing here claims Met.
      void qc.invalidateQueries({ queryKey: ['objective-inputs', slug, program, periodId] });
      setRunning(ran ? 'ran' : 'still_running');
      p.announce(ran ? 'Checked just now · the new result comes next hour, then your review' : 'Still collecting · the new result comes next hour, then your review');
    } catch (e) {
      setRunning('error');
      p.announce(errText(e, 'The check could not re-run.'));
    }
  };
  // Flow H (round 2): a person-recorded Not met can be recorded again once fixed (Document | Interview).
  if (decide) return <><DecidePanel {...p} /><button type="button" className="gr-mutedlink" onClick={() => setDecide(false)}>Back to the finding</button></>;
  const unlink = async () => {
    if (!tk) return;
    try {
      await endProgramEntry(slug, program, tk.entry_id, 'unlinked');
      p.announce(`Ticket ${String(tk.data.ticket_key)} unlinked`);
      await Promise.all([qc.invalidateQueries({ queryKey: ['program-entries', slug, program] }), qc.invalidateQueries({ queryKey: ['next-gap', slug, program, periodId] })]);
      focusWhenReady(() => ticketBtn.current);
    } catch (x) { p.announce(errText(x, 'The ticket could not be unlinked.')); }
  };
  return (
    <>
      {p.concern ? <p className="gr-small"><b className="gr-strong">Stage 1 concern:</b>{` ${p.concern}`}</p> : null}
      {tk ? <p className="gr-small" data-part="ticket-tracked">{'Tracked in '}{typeof tk.data.url === 'string' ? <a className="gr-textlink" href={tk.data.url} target="_blank" rel="noreferrer">{String(tk.data.ticket_key)}</a> : <b className="gr-strong">{String(tk.data.ticket_key)}</b>}
        {` · linked ${relDays(tk.recorded_at.slice(0, 10), today())} · `}<button type="button" className="gr-mutedlink" onClick={() => void unlink()}>Unlink</button></p> : null}
      {dets.isLoading ? <Spinner label="Loading the failing result" /> : (
        <p className="gr-small" data-part="fix-finding" tabIndex={-1}><b className="gr-strong">{det?.rationale ?? 'The last result for this point is Not met.'}</b></p>
      )}
      {det ? <p className="gr-small gr-dim">{`${det.kind === 'machine' ? 'Checked' : 'Recorded'} ${fmtDate(det.proposed_at, today())}${src ? ` · read-only, so the fix happens in ${connectorWord(src.kind)}` : ''}`}</p> : null}
      {running === 'running' ? <div className="v6-result" data-tone="wait" role="status"><b>Re-running the check…</b><span className="gr-small gr-dim">collecting now</span></div> : null}
      {running === 'still_running' ? <div className="v6-result" data-tone="wait" role="status"><b>Still collecting</b><span className="gr-small gr-dim">the new result comes next hour, then your review</span></div> : null}
      {running === 'ran' ? <div className="v6-result" data-tone="wait" role="status"><b>Checked just now</b><span className="gr-small gr-dim">the new result comes next hour, then your review</span></div> : null}
      {running === 'error' ? <div className="v6-result" data-tone="bad" role="alert"><b><span aria-hidden="true">✕ </span>The check could not re-run</b><span className="gr-small gr-dim">try again, or create a ticket</span></div> : null}
      {canEvidence(p) ? (
        <div className="gr-actions">
          {src && p.isAdmin ? <ActButton compact onClick={() => void rerun()} aria-disabled={running === 'running' || undefined}>Re-run the check</ActButton> : null}
          {!src || (det && det.kind !== 'machine') ? <ActButton compact onClick={() => setDecide(true)}>Record a new result</ActButton> : null}
          {p.program === 'iso-42001' ? <Link className="gp-linkbtn gp-linkbtn--compact" to={`${p.prefix}/policies?return=${encodeURIComponent(`${p.prefix}/programs/${program}/prepare#${o.anchor}`)}`}>Open the procedure in Policies</Link> : null}
          <ActButton compact ref={ticketBtn} aria-expanded={sub === 'ticket'} aria-controls={`tk-${o.anchor}`} onClick={() => setSub(sub === 'ticket' ? null : 'ticket')}>{tk ? 'Replace the ticket' : 'Create a ticket'}</ActButton>
          {p.tracking.risk.has(o.row.objective_id) ? null : <ActButton compact variant="ghost" aria-expanded={sub === 'risk'} aria-controls={`ra-${o.anchor}`} onClick={() => setSub(sub === 'risk' ? null : 'risk')}>Accept the risk</ActButton>}
        </div>
      ) : <GateFor p={p} verb="fixes this" />}
      {canEvidence(p) && (p.program === 'iso-42001' || !src) ? <UploadEvidence p={p} label={p.program === 'iso-42001' ? 'Revised procedure' : 'Evidence of the fix'} hint={`Counts for ${o.label}${p.concern ? ' and answers the stage 1 concern' : ''}`} done={p.program === 'iso-42001' ? 'Revised procedure added · record a new result with it next' : 'Evidence added · record a new result with it next'} /> : null}
      {sub === 'ticket' ? <TicketForm p={p} /> : null}
      {sub === 'risk' ? <RiskForm p={p} /> : null}
    </>
  );
}

function TicketForm({ p }: { p: PanelProps }) {
  const { o } = p;
  const [key, setKey] = useState('');
  const [url, setUrl] = useState('');
  const [err, setErr] = useState<{ key?: string; url?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);
  const summary = `${o.label}: ${o.row.statement}`;
  const keyRef = useRef<HTMLInputElement>(null);
  useEffect(() => { keyRef.current?.focus(); }, []);
  const submit = async () => {
    if (busy) return;
    const e: typeof err = {};
    if (!key.trim()) e.key = 'Enter the ticket key, e.g. NW-482';
    if (url.trim() && !/^https:\/\//.test(url.trim())) e.url = 'Use an https link';
    setErr(e);
    if (e.key || e.url) { p.announce(e.key ?? e.url!); (e.key ? keyRef.current : null)?.focus(); return; }
    setBusy(true);
    try {
      const entry = await recordProgramEntry(p.slug, p.program, { kind: 'objective_ticket', subject_key: o.row.objective_id, data: { ticket_key: key.trim(), ...(url.trim() ? { url: url.trim() } : {}) } });
      p.onHandled(o, { spec: { text: `Ticket ${key.trim()} linked · ${o.label} is in progress`, undo: () => endProgramEntry(p.slug, p.program, entry.entry_id, 'undo').then(() => undefined) }, kindAfter: 'progress', meta: `ticket ${key.trim()} · in progress` });
    } catch (x) { setErr({ form: errText(x, 'The ticket could not be linked.') }); } finally { setBusy(false); }
  };
  return (
    <div className="v6-act-row" id={`tk-${o.anchor}`} data-part="ticket-form">
      <Field id={`tk-s-${o.anchor}`} label="Ticket summary" hint="Create the ticket in your tracker with this summary, then enter its key · GAC Pro never writes to your tracker"><Input readOnly value={summary} onFocus={(e) => e.currentTarget.select()} /></Field>
      <ActButton compact variant="ghost" onClick={() => { void navigator.clipboard?.writeText(summary).then(() => p.announce('Summary copied'), () => p.announce('Copy the summary from the field')); }}>Copy summary</ActButton>
      <Field id={`tk-k-${o.anchor}`} label="Ticket key" required error={err.key} errorRole="none"><Input ref={keyRef} value={key} onChange={(e) => setKey(e.currentTarget.value)} placeholder="NW-482" /></Field>
      <Field id={`tk-u-${o.anchor}`} label="Link (optional)" error={err.url} errorRole="none"><Input value={url} onChange={(e) => setUrl(e.currentTarget.value)} placeholder="https://" /></Field>
      <ActButton compact onClick={() => void submit()} aria-disabled={busy || undefined}>{busy ? 'Saving…' : 'Save the ticket key'}</ActButton>
      {err.form ? <p className="v6-err" role="alert">{err.form}</p> : null}
    </div>
  );
}

function RiskForm({ p }: { p: PanelProps }) {
  const { o } = p;
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const submit = async () => {
    if (busy) return;
    // D-106-20: the reason becomes the Risk acceptances justification (the register's 20-character rule).
    if (reason.trim().length < RISK_REASON_MIN) {
      const m = reason.trim() ? `Say a little more (at least ${RISK_REASON_MIN} characters)` : 'Say why the risk is acceptable';
      setErr(m); p.announce(m); ref.current?.focus(); return;
    }
    setBusy(true); setErr(null);
    try {
      const entry = await recordProgramEntry(p.slug, p.program, { kind: 'objective_risk_request', subject_key: o.row.objective_id, data: { reason: reason.trim(), period_id: p.periodId } });
      p.onHandled(o, { spec: { text: 'Sent · an owner or admin decides it on Today', undo: () => endProgramEntry(p.slug, p.program, entry.entry_id, 'undo').then(() => undefined) }, kindAfter: 'progress', meta: 'risk acceptance pending · in progress' });
    } catch (x) { setErr(errText(x, 'The request could not be sent.')); } finally { setBusy(false); }
  };
  return (
    <div className="v6-act-row" id={`ra-${o.anchor}`} data-part="risk-form">
      <Field id={`ra-r-${o.anchor}`} label="Why the risk is acceptable" required hint="Once approved, it is accepted for 90 days" error={err ?? undefined} errorRole="none"><Textarea ref={ref} value={reason} onChange={(e) => setReason(e.currentTarget.value)} rows={3} /></Field>
      <ActButton compact onClick={() => void submit()} aria-disabled={busy || undefined}>{busy ? 'Sending…' : 'Send to risk acceptances'}</ActButton>
    </div>
  );
}

/** Owner / admin: decide a teammate's request to accept a Not met point's risk (flow Blocker, round 2).
 *  D-106-20: accepting also adds the row to Risk acceptances (server, same write). */
function RiskDecision({ p, o, request, who }: { p: CritProps; o: TreeObjective; request: ProgramEntry; who: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const decide = async (decision: 'accepted' | 'declined') => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      await recordProgramEntry(p.slug, p.program, { kind: 'objective_risk_decision', subject_key: request.entry_id, data: { decision } });
      const m = decision === 'accepted' ? `Risk accepted · added to Risk acceptances · ${o.label} stays Not met, in progress` : `Declined · ${o.label} is Not met again`;
      // The row's result (with the ranked next gap) replaces the decision box; onHandled refreshes entries and the gap.
      p.onHandled(o, { spec: { text: m }, kindAfter: decision === 'accepted' ? 'progress' : null, meta: decision === 'accepted' ? 'risk accepted · in progress' : 'risk declined' });
      void qc.invalidateQueries({ queryKey: ['today-programs', p.slug] });
    } catch (x) { setErr(errText(x, 'The decision could not be recorded.')); } finally { setBusy(false); }
  };
  return (
    <div className="v6-act" data-part="risk-decision" role="group" aria-label={`Risk acceptance for ${o.label}`}>
      <p className="gr-small"><b className="gr-strong">{`${who} asked to accept this risk:`}</b>{` “${String(request.data.reason ?? '')}”`}</p>
      <div className="gr-actions">
        <ActButton compact variant="primary" onClick={() => void decide('accepted')} aria-disabled={busy || undefined}>Accept the risk</ActButton>
        <ActButton compact onClick={() => void decide('declined')} aria-disabled={busy || undefined}>Decline</ActButton>
      </div>
      {err ? <p className="v6-err" role="alert">{err}</p> : null}
    </div>
  );
}

// ---- Add evidence: Collect · Connect · Request -------------------------------------------------

function UploadEvidence({ p, label, hint, done }: { p: PanelProps; label: string; hint: string; done: string }) {
  const { o } = p;
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (busy) return;
    if (!file) { setErr('Choose a file to upload'); p.announce('Choose a file to upload'); document.getElementById(`f-${o.anchor}`)?.focus(); return; }
    setBusy(true); setErr(null);
    try {
      const r = await uploadObjectiveEvidence(p.slug, p.program, p.periodId, { objectiveId: o.row.objective_id, file, testKey: p.inputs.get(o.row.objective_id)?.test_key ?? null });
      // D-106-19: a file is judged by a person (the hourly run reads collector results), so the result offers Decide.
      p.onHandled(o, { spec: { text: done, undo: () => deleteEvidenceArtifact(p.slug, r.artifact.artifact_id).then(() => undefined), then: { label: 'Decide now', onClick: () => p.reopenDecide(o, r.artifact.artifact_id) } }, kindAfter: 'decide', meta: `${r.artifact.title} · just now` });
    } catch (x) { setErr(errText(x, 'The upload failed.')); } finally { setBusy(false); }
  };
  return (
    <div className="v6-upload" data-part="collect">
      <FileField id={`f-${o.anchor}`} label={label} hint={hint} error={err} onFile={(f) => { setFile(f); setErr(null); }} />
      <ActButton variant="primary" onClick={() => void submit()} aria-disabled={busy || undefined}>{busy ? 'Uploading…' : 'Upload'}</ActButton>
    </div>
  );
}

function ConnectTab({ p, sources }: { p: PanelProps; sources: CollectSource[] }) {
  const [busy, setBusy] = useState<string | null>(null);
  const ret = `${p.prefix}/programs/${p.program}/prepare#${p.o.anchor}`;
  if (!sources.length) return <p className="gr-small">No connector covers this · collect a file or request it from the owner</p>;
  return (
    <div className="gr-rows">
      {sources.map((s) => (
        <div key={s.source} className="v6-conn">
          <span><b>{connectorWord(s.kind ?? (s.source === 'Cloud control plane' ? 'aws' : s.source === 'Identity provider / directory' ? 'okta_posture' : null) ?? s.source)}</b>{s.connector_id ? ' · connected · collect this now' : ' · not connected'}</span>
          {s.connector_id ? (p.isAdmin
            ? <button type="button" className="gp-linkbtn gp-linkbtn--compact btn-primary" data-part="turn-on" aria-disabled={busy === s.connector_id || undefined}
                onClick={async () => {
                  if (busy) return;
                  setBusy(s.connector_id);
                  try { await runPostureConnector(p.slug, s.connector_id!); p.onHandled(p.o, { spec: { text: p.inputs.get(p.o.row.objective_id)?.reason === 'person_decides' ? `Collecting from ${connectorWord(s.kind)} · then decide this point` : `Collecting from ${connectorWord(s.kind)} · results next hour, then your review`, tone: 'ok' }, kindAfter: 'pending', meta: `${connectorWord(s.kind)} · collecting` }); }
                  catch (x) { p.announce(errText(x, 'The check could not start.')); } finally { setBusy(null); }
                }}>Collect now<span className="gp-sr-only">{` from ${connectorWord(s.kind)}`}</span></button>
            : <Gate id={`gc-${p.o.anchor}`} who="An owner or admin" verb="collects from connectors" ask={(() => { const a = p.people.find((x) => ['owner', 'admin'].includes(x.role ?? '') && x.user_id !== p.viewerId); return a ? <AskButton slug={p.slug} program={p.program} person={a} about={`Collect ${connectorWord(s.kind)} evidence for ${p.o.label}`} announce={p.announce} /> : null; })()} />)
            : <Link className="gp-linkbtn gp-linkbtn--compact" data-part="connect-link" to={`${p.prefix}/settings/integrations?return=${encodeURIComponent(ret)}`}>Connect<span className="gp-sr-only">{` ${s.source}`}</span></Link>}
        </div>
      ))}
    </div>
  );
}

function RequestTab({ p }: { p: PanelProps }) {
  const others = p.people.filter((x) => x.user_id !== p.viewerId);
  const [who, setWho] = useState(others[0]?.user_id ?? '');
  const [due, setDue] = useState(() => new Date(Date.now() + 11 * 86_400_000).toISOString().slice(0, 10));
  const person = others.find((x) => x.user_id === who) ?? null;
  if (!others.length) return <p className="gr-small">No one else is in this workspace yet · <Link to={`${p.prefix}/settings/team`}>Invite a teammate</Link></p>;
  return (
    <div className="v6-act-row" data-part="request">
      <Field id={`rq-w-${p.o.anchor}`} label="Ask"><Select value={who} onChange={(e) => setWho(e.currentTarget.value)}>{others.map((x) => <option key={x.user_id} value={x.user_id}>{x.name}</option>)}</Select></Field>
      <Field id={`rq-d-${p.o.anchor}`} label="Due"><Input type="date" min={today()} value={due} onChange={(e) => setDue(e.currentTarget.value)} /></Field>
      {person ? <AskButton slug={p.slug} program={p.program} person={person} compact={false} label="Send request" about={`Evidence for ${p.o.label} by ${due}`} announce={p.announce} /> : null}
    </div>
  );
}

function EvidencePanel(p: PanelProps) {
  if (!canEvidence(p)) return <GateFor p={p} verb="adds evidence" />;
  const sources = p.gap?.collect[p.o.row.objective_id] ?? [];
  return <Tabs label={`Add evidence for ${p.o.label}`} idBase={`ev-${p.o.anchor}`} initial={p.initialTab} tabs={[
    { key: 'collect', label: 'Collect', panel: <UploadEvidence p={p} label="File" hint={`PDF, DOCX, MD or TXT · counts for ${p.o.label}`} done="Evidence added · decide this point with it next" /> },
    { key: 'connect', label: 'Connect', panel: <ConnectTab p={p} sources={sources} /> },
    { key: 'request', label: 'Request', panel: <RequestTab p={p} /> },
  ]} />;
}

// ---- Decide: Document · Interview --------------------------------------------------------------

function ResultRadios({ name, value, onChange }: { name: string; value: string; onChange: (v: 'met' | 'not_met' | 'not_applicable') => void }) {
  return (
    <fieldset className="v6-fs v6-fs--row"><legend>Result</legend>
      {([['met', 'Met'], ['not_met', 'Not met'], ['not_applicable', 'Not applicable (give a reason)']] as const).map(([v, l]) => (
        <label key={v} className="v6-radio"><input type="radio" name={name} value={v} checked={value === v} onChange={() => onChange(v)} />{l}</label>
      ))}
    </fieldset>
  );
}

function DecidePanel(p: PanelProps) {
  if (!canEvidence(p)) return <GateFor p={p} verb="records decisions" />;
  return <Tabs label={`Decide ${p.o.label}`} idBase={`dc-${p.o.anchor}`} tabs={[
    { key: 'document', label: 'Document', panel: <DecideDocument p={p} /> },
    { key: 'interview', label: 'Interview', panel: <DecideInterview p={p} /> },
  ]} />;
}

function useDecision(p: PanelProps) {
  const reviewer = p.lead && p.lead.user_id !== p.viewerId ? p.lead.name : 'a lead or admin who didn’t record it';
  return async (result: 'met' | 'not_met' | 'not_applicable', rationale: string, refs: Array<{ kind: string; id: string }>, meta: string) => {
    await proposeDetermination(p.slug, p.program, p.periodId, {
      objective_id: p.o.row.objective_id, result, rationale: rationale.trim() || null, evidence_refs: refs, gap_type: result === 'not_met' ? 'not_implemented' : null,
    });
    p.onHandled(p.o, { spec: { text: `Decision recorded · ${reviewer} reviews it` }, kindAfter: 'pending', meta });
  };
}

function DecideDocument({ p }: { p: PanelProps }) {
  const docs = useQuery({ queryKey: ['evidence-library', p.slug], queryFn: async () => (await import('../../lib/api')).listEvidenceArtifacts<{ artifacts: Array<{ artifact_id: string; title: string; version_label?: string | null }> }>(p.slug), retry: false, staleTime: 60_000 });
  const list = docs.data?.artifacts ?? [];
  const [doc, setDoc] = useState(() => p.decideDoc.get(p.o.row.objective_id) ?? '');
  const [result, setResult] = useState<'met' | 'not_met' | 'not_applicable'>('met');
  const [why, setWhy] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const record = useDecision(p);
  useEffect(() => { if (!doc && list[0]) setDoc(list[0].artifact_id); }, [doc, list]);
  if (docs.isLoading) return <Spinner label="Loading documents" />;
  if (!list.length) return <p className="gr-small">No documents in the Evidence library yet · <Link to={`${p.prefix}/evidence`}>Add a document</Link> or record an interview</p>;
  const submit = async () => {
    if (busy) return;
    if (result !== 'met' && !why.trim()) { setErr(result === 'not_applicable' ? 'Say why it doesn’t apply' : 'Say what the document shows'); document.getElementById(`dw-${p.o.anchor}`)?.focus(); return; }
    setBusy(true); setErr(null);
    try { await record(result, why, [{ kind: 'artifact', id: doc }], `${list.find((d) => d.artifact_id === doc)?.title ?? 'document'} · just now`); }
    catch (x) { setErr(errText(x, 'The decision could not be recorded.')); } finally { setBusy(false); }
  };
  return (
    <div className="v6-act-row" data-part="decide-document">
      <Field id={`dd-${p.o.anchor}`} label="Document"><Select value={doc} onChange={(e) => setDoc(e.currentTarget.value)}>{list.map((d) => <option key={d.artifact_id} value={d.artifact_id}>{d.title}</option>)}</Select></Field>
      <ResultRadios name={`dr-${p.o.anchor}`} value={result} onChange={setResult} />
      {result !== 'met' ? <Field id={`dw-${p.o.anchor}`} label={result === 'not_applicable' ? 'Why it doesn’t apply' : 'What the document shows'} required error={err ?? undefined} errorRole="none"><Textarea value={why} rows={2} onChange={(e) => setWhy(e.currentTarget.value)} /></Field> : null}
      <ActButton compact onClick={() => void submit()} aria-disabled={busy || undefined}>{busy ? 'Recording…' : 'Record'}</ActButton>
      {err && result === 'met' ? <p className="v6-err" role="alert">{err}</p> : null}
    </div>
  );
}

function DecideInterview({ p }: { p: PanelProps }) {
  const [who, setWho] = useState(p.people.find((x) => x.user_id !== p.viewerId)?.user_id ?? '');
  const [on, setOn] = useState(today());
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState<'met' | 'not_met' | 'not_applicable'>('met');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const record = useDecision(p);
  if (!p.canManage) return <Gate id={`gi-${p.o.anchor}`} who={p.program === 'soc2' ? 'The SOC 2 lead or an admin' : 'The ISO lead or an admin'} verb="records interviews"
    ask={p.lead && p.lead.user_id !== p.viewerId ? <AskButton slug={p.slug} program={p.program} person={p.lead} about={`Interview for ${p.o.label}`} announce={p.announce} /> : null} />;
  const submit = async () => {
    if (busy) return;
    if (!notes.trim()) { setErr('Note what you heard'); document.getElementById(`in-${p.o.anchor}`)?.focus(); return; }
    setBusy(true); setErr(null);
    try {
      const person = p.people.find((x) => x.user_id === who);
      const iv = await recordInterview(p.slug, p.program, p.periodId, { interviewee_user_id: who || null, conducted_at: on, topic: `${p.o.label}: ${p.o.row.statement}`.slice(0, 500), objective_ids: [p.o.row.objective_id], notes });
      await record(result, notes, [{ kind: 'interview', id: iv.interview_id }], `interview, ${person?.name ?? 'teammate'} · just now`);
    } catch (x) { setErr(errText(x, 'The interview could not be recorded.')); } finally { setBusy(false); }
  };
  return (
    <div className="v6-act-row" data-part="decide-interview">
      <Field id={`iw-${p.o.anchor}`} label="Who you interviewed"><Select value={who} onChange={(e) => setWho(e.currentTarget.value)}>{p.people.map((x) => <option key={x.user_id} value={x.user_id}>{x.name}</option>)}</Select></Field>
      <Field id={`id-${p.o.anchor}`} label="Date"><Input type="date" value={on} onChange={(e) => setOn(e.currentTarget.value)} /></Field>
      <Field id={`in-${p.o.anchor}`} label="What you heard" required error={err ?? undefined} errorRole="none"><Textarea value={notes} rows={2} onChange={(e) => setNotes(e.currentTarget.value)} /></Field>
      <ResultRadios name={`ir-${p.o.anchor}`} value={result} onChange={setResult} />
      <ActButton compact onClick={() => void submit()} aria-disabled={busy || undefined}>{busy ? 'Recording…' : 'Record interview'}</ActButton>
    </div>
  );
}

export { objectiveAnchor };

// ---- D-106-18: Review proposed results (approve / send back; SoD is the server's) ---------------

/** A proposed row as GET …/determinations returns it (cited evidence named by the server). */
type AssessmentDeterminationRow = {
  determination_id: string; objective_id: string; kind: string; result: string; proposed_by: string;
  evidence?: Array<{ kind: string; id: string; title: string | null; href: string | null; status?: string | null; observed_at?: string | null; source?: string | null }>;
};
/** The review panel's row for an objective (no CSS.escape: ids carry dots and brackets). */
const reviewItem = (objectiveId: string) => [...document.querySelectorAll<HTMLElement>('[data-review-item]')].find((x) => x.dataset.reviewItem === objectiveId) ?? null;
const firstName = (name: string) => name.split(/\s+/)[0] ?? name;
const reviewCta = (n: number) => `Review ${n} proposed ${n === 1 ? 'result' : 'results'}`;

function ReviewPanel(p: {
  slug: string; program: string; periodId: string; prefix: string; tree: Tree; people: Person[]; viewerId: string; announce: (m: string) => void;
  backToToday: boolean; next: NextGap | null; onNext: (objectiveId: string) => void;
  onClose: () => void; onReviewed: () => void;
}) {
  type Row = AssessmentDeterminationRow;
  const q = useQuery({ queryKey: queryKeys.determinations(p.slug, p.program, p.periodId, 'proposed'), queryFn: () => listDeterminations(p.slug, p.program, p.periodId, 'proposed'), retry: false, staleTime: 15_000 });
  const objs = useMemo(() => {
    const m = new Map<string, TreeObjective>();
    for (const g of p.tree.groups) for (const c of g.criteria) for (const o of c.objectives) m.set(o.row.objective_id, o);
    return m;
  }, [p.tree]);
  const nameOf = (uid: string) => (uid === p.viewerId ? 'you' : p.people.find((x) => x.user_id === uid)?.name ?? 'a teammate');
  const all = ((q.data?.determinations ?? []) as Row[]).filter((d) => d.kind === 'self' && objs.has(d.objective_id));
  const mine = all.filter((d) => d.proposed_by === p.viewerId).length;
  // Flow F4: rows handled here stay listed with their result (a refetch drops them from "proposed").
  const [seen, setSeen] = useState<ReadonlyMap<string, Row>>(new Map());
  useEffect(() => {
    const add = all.filter((d) => d.proposed_by !== p.viewerId && !seen.has(d.determination_id));
    if (add.length) setSeen((m) => { const n = new Map(m); for (const d of add) n.set(d.determination_id, d); return n; });
  }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = [...seen.values()];
  const [done, setDone] = useState<ReadonlyMap<string, string>>(new Map());
  const [back, setBack] = useState<string | null>(null);
  const [reasons, setReasons] = useState<ReadonlyMap<string, string>>(new Map());
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<{ id: string; text: string; field: boolean } | null>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const today = new Date().toISOString().slice(0, 10);
  const sodText = (e: unknown, fallback: string) => {
    const code = e instanceof ApiError ? (e.body as { code?: string } | undefined)?.code : undefined;
    return code === 'sod' ? 'You prepared evidence for this result, so someone else reviews it' : errText(e, fallback);
  };
  const left = rows.filter((d) => !done.has(d.determination_id)).length;
  const act = async (d: Row, kind: 'approve' | 'back') => {
    if (busy) return;
    const o = objs.get(d.objective_id)!;
    const reason = (reasons.get(d.determination_id) ?? '').trim();
    if (kind === 'back' && !reason) {
      setErr({ id: d.determination_id, text: 'Say what should change', field: true });
      p.announce('Say what should change');
      window.setTimeout(() => document.getElementById(`rv-r-${d.determination_id}`)?.focus(), 0);
      return;
    }
    setBusy(d.determination_id); setErr(null);
    try {
      if (kind === 'approve') await reviewDetermination(p.slug, p.program, p.periodId, d.determination_id);
      else await sendBackDetermination(p.slug, p.program, p.periodId, d.determination_id, reason);
      const text = kind === 'approve' ? 'Approved · counts now' : 'Sent back · stays open';
      const nowLeft = left - 1;
      setDone((m) => new Map(m).set(d.determination_id, text));
      setBack(null);
      p.announce(`${text} · ${o.row.statement}${nowLeft === 0 ? ' · all reviewed' : ''}`);
      p.onReviewed();
      if (nowLeft === 0) focusWhenReady(() => summaryRef.current);
      else focusWhenReady(() => [...document.querySelectorAll<HTMLElement>('[data-review-item]:not([data-done])')].find((x) => x.dataset.reviewItem !== d.objective_id)?.querySelector<HTMLElement>('button'));
    } catch (e) {
      const text = sodText(e, kind === 'approve' ? 'The result could not be approved.' : 'The result could not be sent back.');
      setErr({ id: d.determination_id, text, field: false });
      p.announce(text);
    } finally { setBusy(null); }
  };
  return (
    <section className="gr-card" id="review-panel" data-part="review-panel" aria-labelledby="rv-h" onKeyDown={(ev) => { if (ev.key === 'Escape' && !(ev.target instanceof HTMLTextAreaElement && ev.target.value)) { ev.preventDefault(); p.onClose(); } }}>
      <div className="gr-card-head">
        <h3 id="rv-h" className="gr-card-h" tabIndex={-1}>Proposed results</h3>
        <span className="gr-actions">
          {p.backToToday ? <Link className="gr-mutedlink" to={`${p.prefix}/today`}>Back to Today</Link> : null}
          <button type="button" className="gr-mutedlink" onClick={p.onClose}>Close</button>
        </span>
      </div>
      {q.isLoading && !rows.length ? <Spinner label="Loading proposed results" /> : q.error && !rows.length ? (
        <Alert variant="error"><p>Proposed results could not load.</p><ActButton compact onClick={() => void q.refetch()}>Retry</ActButton></Alert>
      ) : !rows.length ? (
        <p className="gr-small">{`Nothing waits on your review${mine ? ` · ${mine} you recorded ${mine === 1 ? 'waits' : 'wait'} for a teammate` : ''}`}</p>
      ) : (
        <>
          {left ? <p className="gr-small gr-dim">{`${left} to review`}</p> : (
            <div className="v6-result" data-tone="ok" role="status" tabIndex={-1} ref={summaryRef} data-part="review-done">
              <b><span aria-hidden="true">✓ </span>{`All ${rows.length} reviewed`}</b>
              {p.next ? <button type="button" className="gr-textlink v6-plain" onClick={() => p.onNext(p.next!.objective_id)}>{`Next gap → ${p.next.statement}`}</button> : null}
              {p.backToToday ? <Link className="gr-textlink" to={`${p.prefix}/today`}>Back to Today</Link> : null}
            </div>
          )}
          <ul className="gr-flat" aria-label="Proposed results">
            {rows.map((d) => {
              const o = objs.get(d.objective_id)!;
              const w = proposedResultWords(d, nameOf);
              const fin = done.get(d.determination_id);
              const sending = back === d.determination_id && !fin;
              const evidence = d.evidence ?? [];
              return (
                <li key={d.determination_id} className="gr-ck" data-review-item={d.objective_id} data-done={fin ? 'true' : undefined}>
                  <span className={`gr-g gr-g--${d.result === 'met' ? 'done' : d.result === 'not_met' ? 'not' : 'noev'}`} aria-hidden="true">{d.result === 'met' ? '✓' : d.result === 'not_met' ? '✕' : '·'}</span>
                  <span className="gr-ck-t">{o.row.statement}</span>
                  <span className="gr-ck-m"><b className={d.result === 'not_met' ? 'gr-bad' : 'gr-strong'}>{w.result}</b>{` · ${w.basis} · `}<span className="v6-id">{o.label}</span></span>
                  {fin ? null : (
                    <span className="gr-ck-a">
                      {sending ? null : <ActButton compact variant="primary" onClick={() => void act(d, 'approve')} aria-disabled={busy === d.determination_id || undefined}>{busy === d.determination_id ? 'Approving…' : 'Approve'}<span className="gp-sr-only">{` ${o.label}`}</span></ActButton>}
                      <ActButton compact variant="ghost" aria-expanded={sending} aria-controls={`rv-b-${d.determination_id}`}
                        onClick={() => { setBack(sending ? null : d.determination_id); setErr(null); if (!sending) focusWhenReady(() => document.getElementById(`rv-r-${d.determination_id}`)); }}>
                        {sending ? 'Cancel' : 'Send back'}<span className="gp-sr-only">{` ${o.label}`}</span>
                      </ActButton>
                    </span>
                  )}
                  {evidence.length && !fin ? (
                    <details className="v6-act" data-part="review-evidence">
                      <summary>{`What ${d.proposed_by.startsWith('system:') ? 'the checks saw' : 'they cited'} · ${evidence.length}`}</summary>
                      <ul className="gr-flat">
                        {evidence.map((r) => (
                          <li key={`${r.kind}:${r.id}`} className="gr-small">
                            {r.href ? <Link to={`${p.prefix}${r.href}`}>{evidenceWords(r, today)}</Link> : evidenceWords(r, today)}
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                  {fin ? <div className="v6-result" data-tone="ok" tabIndex={-1}><b><span aria-hidden="true">✓ </span>{fin}</b></div> : null}
                  {sending ? (
                    <div className="v6-act" id={`rv-b-${d.determination_id}`} data-part="send-back" role="group" aria-label={`Send back ${o.label}`}>
                      <Field id={`rv-r-${d.determination_id}`} label="What should change" required error={err?.id === d.determination_id && err.field ? err.text : undefined} errorRole="none"
                        hint={d.proposed_by.startsWith('system:') ? 'The point stays open; the next hourly run checks it again' : `Sent to ${nameOf(d.proposed_by)}; the point stays open until they record it again`}>
                        <Textarea value={reasons.get(d.determination_id) ?? ''} onChange={(e) => { const v = e.currentTarget.value; setReasons((m) => new Map(m).set(d.determination_id, v)); }} rows={2} />
                      </Field>
                      <div className="gr-actions"><ActButton compact variant="primary" onClick={() => void act(d, 'back')} aria-disabled={busy === d.determination_id || undefined}>{busy === d.determination_id ? 'Sending back…' : 'Send back with reason'}<span className="gp-sr-only">{` ${o.label}`}</span></ActButton></div>
                    </div>
                  ) : null}
                  {!fin && err?.id === d.determination_id && !err.field ? <p className="v6-err v6-act" role="alert">{err.text}</p> : null}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
